// FTEDN Creator — Product Lab Page (wired to real data)
import { useEffect, useState } from 'react';
import { useProductStore } from '../../stores/product.store';
import { useBetaStore } from '../../stores/beta.store';
import { ShipmentTracker } from './ShipmentTracker';
import { FeedbackPanel } from './FeedbackPanel';
import { ProductDetail } from './ProductDetail';
import { JournalPanel } from './JournalPanel';
import { Skeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import './ProductLabPage.css';

export function ProductLabPage() {
  const { products, loading, fetchProducts } = useProductStore();
  const { programs, applications, fetchPrograms, fetchMyApplications } = useBetaStore();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
    fetchPrograms();
    fetchMyApplications();
  }, [fetchProducts, fetchPrograms, fetchMyApplications]);

  const activeApps = applications.filter(a => a.status === 'accepted');
  const programProductIds = new Map(programs.map(program => [program.id, program.productId ?? program.id]));
  const activeProductIds = new Set(activeApps.flatMap(app => {
    const productId = programProductIds.get(app.programId);
    return productId ? [app.programId, productId] : [app.programId];
  }));
  const testingProducts = products.filter(p => activeProductIds.has(p.id));
  const selected = selectedId ? testingProducts.find(p => p.id === selectedId) : testingProducts[0];
  const selectedProgram = selected
    ? programs.find(program => program.productId === selected.id || program.id === selected.id)
    : null;
  const selectedApplication = selectedProgram
    ? activeApps.find(app => app.programId === selectedProgram.id)
    : activeApps.find(app => app.programId === selected?.id);
  const selectedProgramId = selectedApplication?.programId ?? selectedProgram?.id ?? selected?.id;

  return (
    <div className="product-lab page-enter">
      <div className="lab-header">
        <h1>Product Lab</h1>
        <p className="lab-subtitle">Your active beta testing products</p>
      </div>

      {loading ? (
        <Skeleton variant="card" count={2} />
      ) : testingProducts.length === 0 ? (
        <EmptyState
          icon="🔬"
          title="No active products"
          description="Join a beta program to start testing products here."
        />
      ) : (
        <>
          {/* Product selector if multiple */}
          {testingProducts.length > 1 && (
            <div className="lab-product-tabs">
              {testingProducts.map(p => (
                <button key={p.id}
                  className={`lab-tab ${selected?.id === p.id ? 'active' : ''}`}
                  onClick={() => setSelectedId(p.id)}>
                  {p.name}
                </button>
              ))}
            </div>
          )}

          {selected && (
            <div className="lab-product-card">
              <div className="lab-product-info">
                <span className="lab-badge">Active</span>
                <h2>{selected.name}</h2>
                <p>{selected.tagline || selected.description}</p>
              </div>

              <div className="lab-sections">
                <ProductDetail product={selected} />
                <ShipmentTracker programId={selectedProgramId} application={selectedApplication} />
                <FeedbackPanel programId={selectedProgramId ?? selected.id} />
                <JournalPanel
                  productId={selected.id}
                  productName={selected.name}
                  programId={selectedProgramId ?? selected.id}
                  startDate={selectedApplication?.appliedAt}
                />
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
